import { determineSelectedManagementOption, determineSelectedOwnerRepOption, isNullUndefinedOrEmptyObject } from '../../../common/utils';
import { mapBulkRenewalFlowConfigToFlowSteps, mapRenewalFlowConfigToFlowSteps } from '../flow/flowThunks';
import { setPaymentFees, setPaymentID, setSignatoryName } from '../payment/paymentSlice';
import {
    resetDocumentUploadProgressStatus,
    resetRenewState,
    setDocumentUploadCompleted,
    setDocumentUploadError,
    setDocumentUploadWorking,
    setInitiatingRenewFlowComplete,
    setInitiatingRenewFlowInProgress,
    setLateRenewalData,
    setRenewApplicationNumber,
    setRenewLicenseCompleted,
    setRenewLicenseError,
    setRenewLicenseInProgress,
    updatePermitData,
} from './renewSlice';
import { getCustomBreakdown } from '../../utils';

export const initiateRenewFlow = (applicationNumber, originPath, onSuccess) => {
    return async (dispatch, getState, api) => {
        try {
            await dispatch(resetRenewState());
            dispatch(setInitiatingRenewFlowInProgress(true));
            dispatch(setRenewApplicationNumber(applicationNumber));

            const { data } = await api.getLicenseAsync(getState(), applicationNumber);

            const lateRenewalFee = getState()?.config?.settings?.express?.registration?.renew?.late_renewal_fee;

            // If there is a late renewal fee, make api call to get required info for text on confirmation page
            if (lateRenewalFee) {
                const { data: lookupData } = await api.getLookupLicenseAsync(getState(), data?.license);
                const lateRenewalData = {
                    hasExpired: lookupData.hasExpired,
                    expirationDate: lookupData.expirationDate,
                    renewBy: lookupData.renewBy
                }
                dispatch(setLateRenewalData(lateRenewalData));
            }

            // make sure the data returned has the correct properties
            if (data.registrantType) {
                const lookupResult = await api.lookupAPNAddress(data.apn);
                let isResidential = false;
                if(lookupResult){
                    isResidential = lookupResult?.parcelSpecificInfo?.residential;
                }
                // get supplementalconfig for each flow
                const supplementalQuestionsConfigApply = getState()?.config?.settings?.express?.registration?.application?.questions;
                const supplementalQuestionsConfigUpdate = getState()?.config?.settings?.express?.registration?.update?.questions;
                const supplementalQuestionsConfigRenew = getState()?.config?.settings?.express?.registration?.renew?.questions;

                const supplementalQuestions = data?.misc?.supplementalQuestions;

                const hasAnswer = (questionKey) => {
                    return supplementalQuestions?.[questionKey]?.answer !== null
                        && supplementalQuestions?.[questionKey]?.answer !== undefined;
                };

                // combine configs
                // We need to do this becuase when we call /renew we need to send through the supplemental questions, otherwise they are cleared
                // put renew flow last since it should be the highest priority
                // the pages (property details, confirmation) where this is used will only display the appropriate questions
                // Don't include questions from other flows if they do not have an answer (if we include them api request will fail)
                const filteredSupplementalQuestionsConfig = [
                    ...(supplementalQuestionsConfigApply || []).filter(
                        (q) => hasAnswer(q.key)
                    ),
                    ...(supplementalQuestionsConfigUpdate || []).filter(
                        (q) => hasAnswer(q.key)
                    ),
                    ...(supplementalQuestionsConfigRenew || [])
                ];

                const uniqueSupplementalQuestions = filteredSupplementalQuestionsConfig.reduce((acc, current) => {
                    const existingIndex = acc.findIndex(item => item.key === current.key);
                    // If key does not exist add it else replace existing value
                    if (existingIndex === -1) {
                        acc.push(current);
                    } else {
                        acc[existingIndex] = current;
                    }
                    return acc;
                }, []);

                const supplementalQuestionResults = data.misc?.supplementalQuestions;

                const propertyDetails = {};

                if (!isNullUndefinedOrEmptyObject(filteredSupplementalQuestionsConfig)) {
                    propertyDetails.supplementalQuestions = {};

                    uniqueSupplementalQuestions.forEach(question => {
                        const key = question.key;

                        // Populate supplemental questions object
                        propertyDetails.supplementalQuestions[key] = {
                            question: question.title,
                            questionSubtext: question.description,
                            ref: key,
                            answer: supplementalQuestionResults?.[key]?.answer,
                        };

                        // Populate top level fields
                        propertyDetails[key] = supplementalQuestionResults?.[key]?.answer
                    });
                }

                const selectedManagementOption = determineSelectedManagementOption(data.people);
                const selectedOwnerRepOption = determineSelectedOwnerRepOption(data.people);

                const parsedData = {
                    property: {
                        propertyaddress: data.address,
                        propertyid: data.apn,
                        unitnumber: data.unitNumber,
                        rentalstructure: data.rentalStructure,
                        isResidential
                    },
                    registrant: {
                        registrantType: data.registrantType,
                        owner: Array.isArray(data.people.owner) ? data.people.owner : [data.people.owner], // // Pages expect this to be an array, but towns that don't allows multiple owners return this as a single object
                        registrant: data.people.registrant
                    },
                    contacts: {
                        selectedManagementOption: selectedManagementOption,
                        selectedOwnerRepOption: selectedOwnerRepOption,
                        propertyManager: data.people?.propertyManager,
                        localContact: data.people?.localContact
                    },
                    propertyDetails,
                }

                dispatch(updatePermitData(parsedData));
                dispatch(mapRenewalFlowConfigToFlowSteps(parsedData, onSuccess, originPath));
                dispatch(setInitiatingRenewFlowComplete(""));
            }
            else {
                dispatch(setInitiatingRenewFlowComplete("Unable to initiate renew flow due to missing data. Please contact Deckard."));
            }

        } catch (err) {
            dispatch(setInitiatingRenewFlowComplete("Something went wrong while fetching required data."));
        }
    }
}

export const initiateBulkRenewFlow = (originPath, onSuccess) => {
    return async (dispatch, getState, api) => {
        try {
            await dispatch(resetRenewState());
            dispatch(setInitiatingRenewFlowInProgress(true));
            dispatch(mapBulkRenewalFlowConfigToFlowSteps(onSuccess, originPath));
            dispatch(setInitiatingRenewFlowComplete(""));
        } catch (err) {
            dispatch(setInitiatingRenewFlowComplete("Something went wrong while fetching required data."));
        }
    }
}

export const renewLicense = (onCompletionCallback) => {
    return async (dispatch, getState, api) => {
        let wasSuccesfull = true;
        let requiresPayment = true;
        let hasDocumentUploadError = false;

        try {
            dispatch(setRenewLicenseInProgress());

            // GET data from Redux
            const applicationNumber = getState().renew?.applicationNumber;
            const { documents, certifications } = getState().renew?.applicationData;

            // RENEW LICENSE
            await api.renewLicense(getState());

            // CALCULATE PAYMENT
            const paymentCalculation = await api.calculateLicenseRenwalFee(applicationNumber);
            const {
                paymentId,
                summary,
                ccFee,
                achFee,
                ccTotal,
                achTotal,
                checkFee,
                checkTotal,
                details,
            } = paymentCalculation;

            const customBreakdown = getCustomBreakdown(details)
            
            const { fee, custom_fee, fee_override, custom_fee_multiple_answers } = getState()?.config?.settings?.express?.registration?.renew;
            const licenseVerbiage = getState()?.config?.settings?.express?.registration?.type_verbiage;

            requiresPayment = (fee && fee !== 0) || custom_fee || fee_override || custom_fee_multiple_answers;

            if (summary.total === 0) {
                requiresPayment = false;
            }

            let breakdown = [];

            // include a late fees line item if renewal is late
            if (summary.latePenalties && summary.latePenalties > 0) {
                const licenseFeeExcludingLateFees = summary.total - summary.latePenalties;
                if(customBreakdown.length > 0) {
                    breakdown = customBreakdown;
                } else {
                    breakdown = [{
                        key: 'licenseFee',
                        label: `${licenseVerbiage} Fee`,
                        value: licenseFeeExcludingLateFees
                    }];
                }
                breakdown.push({
                    key: 'latePenalties',
                    label: 'Late Renewal Fee',
                    value: summary.latePenalties
                });
            } else {
                if(customBreakdown.length > 0) {
                    breakdown = customBreakdown;
                } else {
                    breakdown.push({
                        key: 'licenseFee',
                        label: `${licenseVerbiage} Fee`,
                        value: summary.total
                    });
                }
            }

            const paymentFee = {
                ccFee,
                ccTotal,
                achFee,
                achTotal,
                checkFee,
                checkTotal,
                breakdown
            }

            dispatch(setPaymentID(paymentId));
            dispatch(setPaymentFees(paymentFee));
            dispatch(setSignatoryName(certifications.signatory));

            // UPLOAD DOCUMENTS
            if (documents) {
                try {
                    // Each type of document may have zero or more associated files
                    // but requires only one upload token per type
                    // So we will store the configuration details for each document type in an array
                    const uploadConfiguration = []

                    // Get a list of the document types (documents are stored in Redux with the document type as their key)
                    const documentTypes = Object.keys(documents)
                    // Upload the documents using the new license number
                    for (const documentType in documentTypes) {

                        const docTypeName = documentTypes[documentType]
                        // Get an upload token for each document type
                        const { uploadToken } = await api.fetchDocumentUploadToken(
                            applicationNumber,
                            docTypeName
                        );

                        // Store the token and associated files in the array as its own config object
                        uploadConfiguration.push({
                            document: documentType,
                            uploadToken: uploadToken,
                            files: documents[docTypeName],
                        });
                    }

                    if (
                        !uploadConfiguration ||
                        uploadConfiguration.length === 0 ||
                        uploadConfiguration.filter(
                            (config) => config.files.length === 0
                        ).length === uploadConfiguration.length
                    ) {
                        // If there is no uploadConfiguration, it's empty or it only contains configs with no associated files, then we're done.
                        dispatch(setDocumentUploadCompleted(true));
                    } else {
                        dispatch(setDocumentUploadWorking(true));
                        // Do the upload
                        for (const uploadConfig of uploadConfiguration) {
                            const { files } = uploadConfig;
                            const { attachedFiles } = files;
                            for (const documentFile of attachedFiles) {
                                const { blobFile } = documentFile;
                                await api.uploadDocument({
                                    uploadToken: uploadConfig.uploadToken,
                                    file: blobFile,
                                })
                            }
                        }
                    }
                }
                catch (err) {
                    wasSuccesfull = false;
                    hasDocumentUploadError = true;
                }
                if (hasDocumentUploadError) {
                    dispatch(setDocumentUploadError("An unexpected error occurred while attempting to upload documents."))
                }
            }
        } catch (err) {
            dispatch(resetDocumentUploadProgressStatus());
            dispatch(setRenewLicenseError("An unexpected error occurred while attempting to renew."));
            wasSuccesfull = false;
        }
        finally {
            dispatch(setDocumentUploadCompleted(true));
            dispatch(setRenewLicenseCompleted());
            onCompletionCallback(wasSuccesfull, requiresPayment);
        }
    }
}

export const bulkRenewLicenses = (onCompletionCallback) => {
    return async (dispatch, getState, api) => {
        let requiresPayment = true;
        const { certifications } = getState().renew?.applicationData;

        try {
            dispatch(setRenewLicenseInProgress());

            // RENEW LICENSE
            const {apiResponse, licenseNumbers} = await api.bulkRenewLicenses(getState());

            // CALCULATE PAYMENT
            const paymentCalculation = await api.calculateBulkLicenseRenewalFee(licenseNumbers, getState());
            const {
                paymentId,
                summary,
                ccFee,
                achFee,
                ccTotal,
                achTotal
            } = paymentCalculation;


            const { fee, custom_fee, fee_override, custom_fee_multiple_answers } = getState()?.config?.settings?.express?.registration?.renew;
            const licenseVerbiage = getState()?.config?.settings?.express?.registration?.type_verbiage;

            requiresPayment = (fee && fee !== 0) || custom_fee || fee_override || custom_fee_multiple_answers;

            if (summary.total === 0) {
                requiresPayment = false;
            }

            const paymentFee = {
                ccFee,
                ccTotal,
                achFee,
                achTotal,
                breakdown: [{
                    key: 'licenseFee',
                    label: `${licenseVerbiage} Fee`,
                    value: summary.total
                }]
            }

            dispatch(setPaymentID(paymentId));
            dispatch(setPaymentFees(paymentFee));
            dispatch(setSignatoryName(certifications.signatory));
            dispatch(setRenewLicenseCompleted());
            onCompletionCallback(true, requiresPayment);
        } catch (err) {
            dispatch(setRenewLicenseCompleted());
            onCompletionCallback(false, false);
        }
        
    }
}
